/**
 * @engine/input — Vector2Control
 *
 * 2D analog control for sticks, mouse position, touch, etc.
 */

import type { ControlType, Vec2Like, Vector2State } from '../types';
import { InputControl } from './InputControl';
import { AxisControl } from './AxisControl';

/** Default zero vector */
const ZERO: Vec2Like = { x: 0, y: 0 };

/**
 * Vector2 control — 2D analog value (stick, mouse position, etc.)
 */
export class Vector2Control extends InputControl<Vec2Like> implements Vector2State {
  readonly type: ControlType = 'vector2';

  /** Child axis controls for individual X/Y access */
  readonly x: AxisControl;
  readonly y: AxisControl;

  constructor(name: string) {
    super(name, ZERO);
    this.x = new AxisControl(`${name}/x`, true);
    this.y = new AxisControl(`${name}/y`, true);
  }

  // ============================================================================
  // Vector2State interface
  // ============================================================================

  get delta(): Vec2Like {
    return {
      x: this._value.x - this._previousValue.x,
      y: this._value.y - this._previousValue.y,
    };
  }

  get magnitude(): number {
    return Math.sqrt(this._value.x * this._value.x + this._value.y * this._value.y);
  }

  // ============================================================================
  // Override for deep value comparison
  // ============================================================================

  protected override valuesEqual(a: Vec2Like, b: Vec2Like): boolean {
    return a.x === b.x && a.y === b.y;
  }

  // ============================================================================
  // Update
  // ============================================================================

  override update(newValue: Vec2Like, time: number): void {
    super.update(newValue, time);
    this.x.update(newValue.x, time);
    this.y.update(newValue.y, time);
  }

  override reset(): void {
    super.reset();
    this.x.reset();
    this.y.reset();
  }

  override commitFrame(): void {
    super.commitFrame();
    this.x.commitFrame();
    this.y.commitFrame();
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  /**
   * Get normalized direction (magnitude = 1)
   */
  get normalized(): Vec2Like {
    const mag = this.magnitude;
    if (mag < 0.0001) return ZERO;
    return { x: this._value.x / mag, y: this._value.y / mag };
  }

  /**
   * Check if stick is past deadzone
   */
  isActive(deadzone: number = 0.1): boolean {
    return this.magnitude >= deadzone;
  }
}
